import { useState, useEffect, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Facebook, Instagram, Twitter, Rss, Share2, RefreshCw, Loader2, ImageIcon, PlusCircle, Copy, Download, ExternalLink, Hash } from "lucide-react";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter,
  AlertDialogTitle, AlertDialogDescription, AlertDialogAction, AlertDialogCancel,
} from "@/components/ui/alert-dialog";

const CHANNELS = [
  { key: "facebook", label: "Facebook", icon: Facebook, color: "#1877F2", profileKey: "facebook_page_url", add: "Ajouter ma page Facebook", limit: 63206 },
  { key: "instagram", label: "Instagram", icon: Instagram, color: "#E4405F", profileKey: "instagram_handle", add: "Ajouter mon compte Instagram", limit: 2200 },
  { key: "x", label: "X", icon: Twitter, color: "#0F0F0F", profileKey: "x_handle", add: "Ajouter mon compte X", limit: 280 },
  { key: "substack", label: "Substack", icon: Rss, color: "#FF6719", profileKey: "substack_url", add: "Ajouter ma publication Substack", limit: null },
];

const variantToDraft = (ch, v) => {
  if (!v) return {};
  if (ch === "instagram") return { caption: v.caption || "", hashtags: (v.hashtags || []).map((h) => "#" + h).join(" ") };
  if (ch === "substack") return { title: v.title || "", subtitle: v.subtitle || "", body: v.body || "" };
  return { body: v.body || "" };
};

const wordCount = (s) => (s || "").trim().split(/\s+/).filter(Boolean).length;

/**
 * Composant UNIQUE partagé (Atelier + Mes posts) pour décliner un post sur d'autres réseaux.
 * postId null -> boutons grisés (Atelier : « Enregistrez d'abord en brouillon pour décliner »).
 */
export const DeclinerPost = ({ postId = null, imageUrl = null, context = "posts" }) => {
  const [kit, setKit] = useState(null);
  const [post, setPost] = useState(null);
  const [active, setActive] = useState(null);
  const [gen, setGen] = useState(null);
  const [draft, setDraft] = useState({});
  const [confirmCh, setConfirmCh] = useState(null);
  const [shareFiles, setShareFiles] = useState(false);   // canShare avec fichier (mobile passe, Chrome desktop échoue)
  const [shareTextTouch, setShareTextTouch] = useState(false); // partage texte-seul, uniquement sur appareil tactile

  useEffect(() => {
    try {
      const hasShare = typeof navigator !== "undefined" && !!navigator.share;
      const touch = typeof window !== "undefined" &&
        ((window.matchMedia && window.matchMedia("(pointer: coarse)").matches) ||
          navigator.maxTouchPoints > 0 || "ontouchstart" in window);
      let files = false;
      if (hasShare && navigator.canShare) {
        try { files = navigator.canShare({ files: [new File([new Blob([""])], "x.jpg", { type: "image/jpeg" })], text: "x" }); }
        catch { files = false; }
      }
      const textCap = hasShare && (!navigator.canShare || navigator.canShare({ text: "x" }));
      setShareFiles(!!files);
      setShareTextTouch(!!(textCap && touch));
    } catch { setShareFiles(false); setShareTextTouch(false); }
  }, []);

  useEffect(() => { api.get("/brand-kit").then((r) => setKit(r.data)).catch(() => {}); }, []);

  const reload = useCallback(() => {
    if (!postId) return;
    api.get(`/posts/${postId}`).then((r) => {
      setPost(r.data);
      setDraft((d) => {
        const nd = { ...d };
        Object.entries(r.data.variants || {}).forEach(([ch, v]) => { if (!nd[ch]) nd[ch] = variantToDraft(ch, v); });
        return nd;
      });
    }).catch(() => {});
  }, [postId]);

  // Résilience : recharge à l'ouverture ET au retour sur l'onglet (une requête a pu mourir alors que la variante est en base).
  useEffect(() => { reload(); }, [reload]);
  useEffect(() => {
    const onFocus = () => reload();
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onFocus);
    return () => { window.removeEventListener("focus", onFocus); document.removeEventListener("visibilitychange", onFocus); };
  }, [reload]);

  const variants = post?.variants || {};
  const xLimit = kit?.x_premium ? 25000 : 280;

  const doGenerate = async (ch) => {
    if (!postId) return;
    setGen(ch);
    try {
      const { data } = await api.post(`/posts/${postId}/variants/${ch}`);
      setPost((p) => ({ ...(p || {}), variants: { ...(p?.variants || {}), [ch]: data.variant }, core_message: data.core_message }));
      setDraft((d) => ({ ...d, [ch]: variantToDraft(ch, data.variant) }));
      setActive(ch);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setGen(null); }
  };

  // Régénération : confirmation maison (AlertDialog) si la variante a été modifiée à la main.
  const requestGenerate = (ch) => {
    if (!postId) return;
    if (variants[ch]?.edited) { setConfirmCh(ch); return; }
    doGenerate(ch);
  };

  const saveField = async (ch, fields) => {
    try {
      const { data } = await api.put(`/posts/${postId}/variants/${ch}`, fields);
      setPost((p) => ({ ...p, variants: { ...p.variants, [ch]: data.variant } }));
      toast.success("Modifications enregistrées");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  // Enregistrement à la perte de focus, uniquement si le champ a changé.
  const blurSave = (ch, field, parse) => (e) => {
    const raw = e.target.value;
    const cur = variants[ch] || {};
    if (field === "hashtags") {
      const arr = raw.split(/[\s,]+/).map((t) => t.replace(/^#/, "").trim()).filter(Boolean);
      if (JSON.stringify(arr) !== JSON.stringify(cur.hashtags || [])) saveField(ch, { hashtags: arr });
      return;
    }
    if ((raw || "") !== (cur[field] || "")) saveField(ch, { [field]: raw });
  };

  const setD = (ch, field, val) => setDraft((d) => ({ ...d, [ch]: { ...(d[ch] || {}), [field]: val } }));

  const toggleImage = async (ch) => {
    const next = !(variants[ch]?.with_image ?? true);
    try {
      const { data } = await api.put(`/posts/${postId}/variants/${ch}`, { with_image: next });
      setPost((p) => ({ ...p, variants: { ...p.variants, [ch]: data.variant } }));
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const profileFilled = (ch) => !!(kit && (kit[ch.profileKey] || "").toString().trim());
  const locked = !postId;

  // --- Phase 4 : passage assisté ---
  const copy = async (text, label) => {
    try { await navigator.clipboard.writeText(text || ""); toast.success(label || "Texte copié"); }
    catch { toast.error("Copie impossible"); }
  };

  const downloadImage = async (ch) => {
    try {
      const res = await fetch(imageUrl);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `pulse-${new Date().toISOString().slice(0, 10)}-${ch}.jpg`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch { toast.error("Téléchargement impossible"); }
  };

  const openUrlFor = (ch) => {
    if (ch === "facebook") return (kit?.facebook_page_url || "").trim() || null;
    if (ch === "instagram") return kit?.instagram_handle ? `https://www.instagram.com/${kit.instagram_handle}` : null;
    if (ch === "substack") { const u = (kit?.substack_url || "").trim(); return u ? `${u.replace(/\/$/, "")}/publish/post` : null; }
    return null;
  };

  const openNetwork = (ch, text) => {
    if (ch === "x") { window.open("https://x.com/intent/post?text=" + encodeURIComponent(text || ""), "_blank", "noreferrer"); return; }
    const url = openUrlFor(ch);
    if (url) window.open(url, "_blank", "noreferrer");
  };

  // Partage natif mobile. Quand « Inclure le visuel » est coupé, on ne transmet que le texte.
  const shareVariant = async (ch, text, withImage) => {
    try {
      let data = { text: text || "" };
      if (withImage && imageUrl) {
        const res = await fetch(imageUrl);
        const blob = await res.blob();
        const file = new File([blob], `pulse-${new Date().toISOString().slice(0, 10)}-${ch}.jpg`, { type: blob.type || "image/jpeg" });
        const withFile = { files: [file], text: text || "" };
        if (navigator.canShare && navigator.canShare(withFile)) data = withFile;
      }
      await navigator.share(data);
    } catch (e) {
      if (e && e.name !== "AbortError") toast.error("Partage indisponible sur cet appareil.");
    }
  };

  const inputCls = "w-full border border-border rounded-lg px-3 py-2 text-sm outline-none focus:ring-1 focus:ring-coral focus:border-coral bg-white";
  const actionCls = "inline-flex items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-ink hover:border-coral transition-colors";

  return (
    <div data-testid="decliner-post" className="mt-4 border-t border-slate-200 pt-4">
      <div className="flex items-center gap-2 mb-3 text-coral-dark">
        <Share2 className="h-4 w-4" />
        <span className="text-xs font-bold uppercase tracking-[0.15em]">Décliner ce post</span>
      </div>

      {/* Barre de canaux — défile horizontalement sur mobile */}
      <div className="flex gap-2 overflow-x-auto whitespace-nowrap pb-1 -mx-1 px-1">
        {CHANNELS.map((c) => {
          const Icon = c.icon;
          const generated = !!variants[c.key];
          const enabled = !locked && profileFilled(c);
          const isActive = active === c.key;
          const busy = gen === c.key;
          return (
            <button key={c.key} data-testid={`decliner-btn-${c.key}`}
              onClick={() => (generated ? setActive(c.key) : enabled ? requestGenerate(c.key) : null)}
              disabled={busy || (!generated && !enabled)}
              className={`shrink-0 inline-flex items-center gap-1.5 rounded-full border px-3.5 py-1.5 text-xs font-semibold transition-colors
                ${isActive ? "bg-coral text-white border-coral"
                  : generated ? "bg-white text-ink border-coral/40 hover:border-coral"
                  : enabled ? "bg-white text-ink border-slate-300 hover:border-coral"
                  : "bg-slate-50 text-slate-400 border-slate-200 cursor-not-allowed"}`}>
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Icon className="h-3.5 w-3.5" style={isActive ? {} : { color: enabled || generated ? c.color : undefined }} />}
              {generated ? c.label : busy ? "Génération…" : `Générer pour ${c.label}`}
            </button>
          );
        })}
      </div>

      {locked && (
        <p data-testid="decliner-locked-note" className="mt-2 text-xs italic text-slate-500">
          Enregistrez d'abord en brouillon pour décliner.
        </p>
      )}

      {/* Liens « Ajouter… » pour les canaux au profil vide (jamais masqués). Si les 4 manquent : un seul lien. */}
      {!locked && (() => {
        const missing = CHANNELS.filter((c) => !profileFilled(c) && !variants[c.key]);
        if (missing.length === 0) return null;
        if (missing.length === CHANNELS.length) {
          return (
            <div className="mt-2">
              <a data-testid="decliner-add-all" href="/marque"
                className="inline-flex items-center gap-1 text-xs text-coral-dark hover:underline">
                <PlusCircle className="h-3 w-3" /> Renseigner mes réseaux dans Ma marque
              </a>
            </div>
          );
        }
        return (
          <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1">
            {missing.map((c) => (
              <a key={c.key} data-testid={`decliner-add-${c.key}`} href="/marque"
                className="inline-flex items-center gap-1 text-xs text-coral-dark hover:underline">
                <PlusCircle className="h-3 w-3" /> {c.add}
              </a>
            ))}
          </div>
        );
      })()}

      {/* Panneau du canal actif */}
      {active && variants[active] && (() => {
        const c = CHANNELS.find((x) => x.key === active);
        const v = variants[active];
        const dr = draft[active] || {};
        const withImage = v.with_image ?? true;
        const limit = active === "x" ? xLimit : c.limit;
        const body = dr.body ?? "";
        const cap = dr.caption ?? "";
        const count = active === "instagram" ? cap.length : body.length;
        const over = limit && count > limit;
        const currentText = active === "instagram" ? cap : body;
        const openUrl = openUrlFor(active);
        const tagsArr = (dr.hashtags ?? "").split(/[\s,]+/).map((t) => t.replace(/^#/, "").trim()).filter(Boolean);
        return (
          <div data-testid={`variant-panel-${active}`} className="mt-4 bg-slate-50/70 border border-slate-200 rounded-xl p-4 space-y-3">
            {active === "instagram" ? (
              <>
                <textarea data-testid={`variant-editor-${active}`} rows={6} className={inputCls}
                  value={cap} onChange={(e) => setD(active, "caption", e.target.value)} onBlur={blurSave(active, "caption")} />
                <div data-testid={`variant-counter-${active}`} className={`text-xs ${over ? "text-red-600 font-semibold" : "text-slate-400"}`}>
                  {count} / {limit} caractères
                </div>
                <div>
                  <label className="text-xs font-bold uppercase tracking-wider text-slate-500">Hashtags</label>
                  <textarea data-testid={`variant-hashtags-${active}`} rows={2} className={`${inputCls} mt-1`}
                    value={dr.hashtags ?? ""} onChange={(e) => setD(active, "hashtags", e.target.value)} onBlur={blurSave(active, "hashtags")} />
                </div>
              </>
            ) : active === "substack" ? (
              <>
                <div>
                  <label className="text-xs font-bold uppercase tracking-wider text-slate-500">Titre</label>
                  <input data-testid="variant-substack-title" className={`${inputCls} mt-1`}
                    value={dr.title ?? ""} onChange={(e) => setD(active, "title", e.target.value)} onBlur={blurSave(active, "title")} />
                  <div data-testid="variant-counter-substack-title" className="text-xs text-slate-400 mt-1">{(dr.title ?? "").length} caractères</div>
                </div>
                <div>
                  <label className="text-xs font-bold uppercase tracking-wider text-slate-500">Sous-titre</label>
                  <input data-testid="variant-substack-subtitle" className={`${inputCls} mt-1`}
                    value={dr.subtitle ?? ""} onChange={(e) => setD(active, "subtitle", e.target.value)} onBlur={blurSave(active, "subtitle")} />
                </div>
                <div>
                  <label className="text-xs font-bold uppercase tracking-wider text-slate-500">Corps (Markdown)</label>
                  <textarea data-testid="variant-substack-body" rows={12} className={`${inputCls} mt-1 font-mono text-xs leading-relaxed`}
                    value={dr.body ?? ""} onChange={(e) => setD(active, "body", e.target.value)} onBlur={blurSave(active, "body")} />
                  <div data-testid="variant-counter-substack-body" className="text-xs text-slate-400 mt-1">{wordCount(dr.body)} mots</div>
                </div>
              </>
            ) : (
              <>
                <textarea data-testid={`variant-editor-${active}`} rows={active === "x" ? 4 : 7} className={inputCls}
                  value={body} onChange={(e) => setD(active, "body", e.target.value)} onBlur={blurSave(active, "body")} />
                {active === "facebook" ? (
                  <div data-testid="variant-counter-facebook" className={`text-xs ${count > c.limit ? "text-red-600 font-semibold" : "text-slate-500"}`}>
                    {count} caractères
                    <span className="block text-[11px] text-slate-400 font-normal">Au-delà de ~250 caractères, Facebook replie le texte derrière « voir plus ».</span>
                  </div>
                ) : (
                  <div data-testid={`variant-counter-${active}`} className={`text-xs ${over ? "text-red-600 font-semibold" : "text-slate-400"}`}>
                    {count} / {limit} caractères
                  </div>
                )}
              </>
            )}

            {/* Image du post (identique pour tous les canaux) + interrupteur maison « Inclure le visuel » */}
            {imageUrl && (
              <div className="flex items-center gap-3 pt-1">
                <button type="button" data-testid={`variant-image-toggle-${active}`} role="switch" aria-checked={withImage}
                  onClick={() => toggleImage(active)}
                  className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors ${withImage ? "bg-coral-strong" : "bg-slate-300"}`}>
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${withImage ? "translate-x-6" : "translate-x-1"}`} />
                </button>
                <span className="inline-flex items-center gap-1.5 text-xs text-ink"><ImageIcon className="h-3.5 w-3.5" /> Inclure le visuel</span>
                {withImage && (
                  <img data-testid={`variant-image-${active}`} src={imageUrl} alt="visuel" className="h-16 rounded border border-slate-200 object-cover ml-auto" />
                )}
              </div>
            )}

            {/* Passage assisté. Parcours GARANTI (copier + ouvrir) toujours affiché ; « Partager » en PLUS si l'appareil le permet vraiment. */}
            <div className="flex flex-wrap gap-2 pt-1">
              <button data-testid={`variant-copy-${active}`} onClick={() => copy(currentText, "Texte copié")} className={actionCls}>
                <Copy className="h-3.5 w-3.5" /> Copier le texte
              </button>
              {imageUrl && withImage && (
                <button data-testid={`variant-download-${active}`} onClick={() => downloadImage(active)} className={actionCls}>
                  <Download className="h-3.5 w-3.5" /> Télécharger l'image
                </button>
              )}
              {active === "x" ? (
                <button data-testid="variant-open-x" onClick={() => openNetwork("x", currentText)} className={actionCls}>
                  <ExternalLink className="h-3.5 w-3.5" /> Ouvrir X avec mon texte
                </button>
              ) : openUrl ? (
                <button data-testid={`variant-open-${active}`} onClick={() => openNetwork(active)} className={actionCls}>
                  <ExternalLink className="h-3.5 w-3.5" /> {active === "facebook" ? "Ouvrir ma page Facebook" : active === "instagram" ? "Ouvrir Instagram" : "Ouvrir Substack"}
                </button>
              ) : null}
              {active === "instagram" && (
                <button data-testid="variant-copy-hashtags-instagram" onClick={() => copy(tagsArr.map((t) => "#" + t).join(" "), "Hashtags copiés")} className={actionCls}>
                  <Hash className="h-3.5 w-3.5" /> Copier les hashtags
                </button>
              )}
              {((withImage && imageUrl) ? shareFiles : shareTextTouch) && (
                <button data-testid={`variant-share-${active}`} onClick={() => shareVariant(active, currentText, withImage)}
                  className="inline-flex items-center gap-1.5 rounded-lg bg-coral-strong px-3 py-1.5 text-xs font-bold text-white hover:bg-coral-hover transition-colors">
                  <Share2 className="h-3.5 w-3.5" /> Partager
                </button>
              )}
            </div>

            {active === "instagram" && (
              <p className="text-[11px] text-slate-400 italic">
                Pour publier depuis un ordinateur, un compte professionnel ou créateur est recommandé.
                Depuis un téléphone, le bouton Partager ouvre directement Instagram.
              </p>
            )}

            <div className="flex items-center justify-between pt-1 border-t border-slate-200 mt-1">
              <span className="text-[11px] text-slate-400 pt-2">{v.edited ? "Modifié à la main" : "Généré"}</span>
              <button data-testid={`variant-regenerate-${active}`} onClick={() => requestGenerate(active)} disabled={gen === active}
                className="inline-flex items-center gap-1.5 text-xs font-semibold text-coral-dark hover:underline disabled:opacity-50 pt-2">
                {gen === active ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />} Régénérer
              </button>
            </div>
          </div>
        );
      })()}

      <AlertDialog open={!!confirmCh} onOpenChange={(o) => { if (!o) setConfirmCh(null); }}>
        <AlertDialogContent data-testid="regenerate-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Régénérer cette déclinaison ?</AlertDialogTitle>
            <AlertDialogDescription>
              Cette version a été modifiée. La régénérer effacera vos modifications.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="regenerate-cancel">Annuler</AlertDialogCancel>
            <AlertDialogAction data-testid="regenerate-confirm"
              onClick={() => { const ch = confirmCh; setConfirmCh(null); doGenerate(ch); }}>
              Régénérer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
